import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import type { AppError, BackendError } from './errors'

const DEFAULT_TIMEOUT_MS = 15_000
const RETRY_LIMIT = 1

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    __retryCount?: number
  }
}

interface ClientOptions {
  baseURL?: string
  getCurrentUser?: () => string | null
}

export function createApiClient(opts: ClientOptions = {}): AxiosInstance {
  const instance = axios.create({
    baseURL: opts.baseURL ?? '',
    timeout: DEFAULT_TIMEOUT_MS,
    headers: { 'Content-Type': 'application/json' }
  })

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const user = opts.getCurrentUser?.()
    if (user) config.headers.set('X-User', encodeURIComponent(user))
    config.headers.set('X-Client-Version', '3.0.0')
    return config
  })

  instance.interceptors.response.use(
    (res) => res,
    async (err: unknown) => {
      // 取消请求不归一
      if (axios.isCancel(err)) return Promise.reject(err)

      if (!axios.isAxiosError<{ error?: BackendError }>(err)) {
        return Promise.reject({ kind: 'unknown', cause: err } satisfies AppError)
      }

      const cfg = err.config as InternalAxiosRequestConfig | undefined

      // 仅 GET + 网络层错误重试一次
      const isNetwork = !err.response
      const isGet = (cfg?.method ?? 'get').toLowerCase() === 'get'
      if (isNetwork && isGet && cfg && (cfg.__retryCount ?? 0) < RETRY_LIMIT) {
        cfg.__retryCount = (cfg.__retryCount ?? 0) + 1
        return instance.request(cfg)
      }

      return Promise.reject(toAppError(err))
    }
  )

  return instance
}

function toAppError(err: AxiosError<{ error?: BackendError }>): AppError {
  if (!err.response) {
    return { kind: 'network', cause: err, retryable: true }
  }
  const status = err.response.status
  const body = err.response.data?.error
  if (status === 401 || status === 403) {
    return { kind: 'auth', status, message: body?.message ?? '未授权' }
  }
  if (body?.code) {
    return {
      kind: 'business',
      code: body.code,
      message: body.message,
      details: body.details,
      traceId: body.traceId
    }
  }
  return { kind: 'unknown', cause: err }
}

/** 默认实例：开发期通过 vite proxy 转发到 :8080，生产由部署侧决定 baseURL */
export const apiClient = createApiClient({
  baseURL: (typeof process !== 'undefined' && process.env?.SSG)
    ? (import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:3001')
    : '',
  getCurrentUser: () => (typeof localStorage !== 'undefined' ? localStorage.getItem('lfr.currentUser') : null)
})
